import Vue from 'vue'
import { API_URL } from '@/config'

export const sessions = {

  namespaced: true,

  state: {
    filters: [
      { column: 'id', label: 'id', options: { type: false }, value: '', status: true },
      { column: 'code', label: 'code', options: { type: false }, value: '', status: true },
      { column: 'title', label: 'title', options: { type: false }, value: '', status: true },
    ],
    options: {
      id: { label: 'id', element: 'text' },
      code: { label: 'code', element: 'text' },
      title: { label: 'title', element: 'text' },
    },
    presets: [],
    columns: [],
    sessions: [],
    imported: {},
    sort: {
      column: 'id',
      columns: [
        { column: 'id', order: 'asc' },
      ],
      order: 'asc'
    },
    total: 0,
    subtotal: 0,
    offset: 0,
  },

  getters: {
    filters: state => state.filters,
    options: state => state.options,
    presets: state => state.presets,
    columns: state => state.columns,
    sessions: state => state.sessions,
    imported: state => state.imported,
    sort: state => state.sort,
    total: state => state.total,
    subtotal: state => state.subtotal,
    offset: state => state.offset,
  },

  mutations: {
    setFilters(state, filters) { state.filters = filters },
    setOptions(state, options) { state.options = options },
    setPresets(state, presets) { state.presets = presets },
    setColumns(state, columns) { state.columns = columns },
    setDays(state, days) {
      state.options['day_id'] = {
        column: 'day_id',
        format: 'date',
        label: 'day',
        date: true,
        element: 'select',
        options: days.reduce((carry, day) => carry.concat([{ value: day.id, label: 'day', name: day.date }]), []),
      }
    },
    setRooms(state, rooms) {
      state.options['room_id'] = {
        column: 'room_id',
        format: null,
        label: 'room',
        element: 'select',
        options: rooms.reduce((carry, room) => carry.concat([{ value: room.id, label: 'room', name: room.name }]), []),
      }
    },
    setSessions(state, sessions) { state.sessions = state.sessions.concat(sessions) },
    setImported(state, imported) { state.imported = imported },
    setSort(state, sort) { state.sort = sort },
    setTotal(state, total) { state.total = total },
    setSubtotal(state, subtotal) { state.subtotal = subtotal },
    setOffset(state, offset) { state.offset = offset },
    clearFilters(state) {
      state.filters = [
        { column: 'id', label: 'id', options: { type: false }, value: '', status: true },
        { column: 'code', label: 'code', options: { type: false }, value: '', status: true },
        { column: 'title', label: 'title', options: { type: false }, value: '', status: true },
      ];
      state.options = {
        'id': { label: 'id', element: 'text' },
        'code': { label: 'code', element: 'text' },
        'title': { label: 'title', element: 'text' },
      };
    },
    clearList(state) {
      state.sessions = [];
      state.total = 0;
      state.subtotal = 0;
      state.offset = 0;
    },
  },

  actions: {

    getOptions: () => Vue.axios.get(API_URL + '/entities', { params: {
      models: ['App\\Models\\Session'],
      filter_display: true,
    }}),

    getDays: () => Vue.axios.get(API_URL + '/days', { params: {
      sorts: [
        { 'column': 'days.order', 'order': 'asc'}
      ],
    }}),

    getRooms: () => Vue.axios.get(API_URL + '/rooms', { params: {
      sorts: [
        { 'column': 'rooms.order', 'order': 'asc'}
      ],
    }}),

    getColumns: () => Vue.axios.get(API_URL + '/entities', { params: {
      models: ['App\\Models\\Session'],
      list_display: true,
    }}),

    getSessions: ({ rootState, getters }) => Vue.axios.get(API_URL + '/sessions', { params: {
      filters: (['admin','user', 'secretariat'].includes(rootState.user.role_id)) ? getters.filters : [{ column: 'private', value: false, status: true }].concat(getters.filters),
      offset: getters.offset,
      sorts: getters.sort.columns,
    }}),

    async getList({ rootState, state, getters, commit, dispatch }) {
      const [options, days, rooms, columns, sessions] = await Promise.all([
        dispatch('getOptions'),
        dispatch('getDays'),
        dispatch('getRooms'),
        dispatch('getColumns'),
        dispatch('getSessions'),
      ])
      .catch(() => {
        commit('setProgress', false, { root: true });
      });
      commit('setProgress', false, { root: true });
      if (!['admin','user', 'secretariat'].includes(rootState.user.role_id)) delete options.data['App\\Models\\Session'].filters['private'];
      commit('setOptions', options.data['App\\Models\\Session'].filters);
      commit('setPresets', options.data['App\\Models\\Session'].presets);
      commit('setDays', days.data.items);
      commit('setRooms', rooms.data.items);
      commit('setColumns', columns.data['App\\Models\\Session']);
      commit('setSessions', sessions.data.items);
      commit('setTotal', sessions.data.total);
      commit('setSubtotal', sessions.data.subtotal);
      commit('setOffset', getters.sessions.length);
      state.options.conflict = {
        format: 'wording',
        label: 'conflicts',
        element: 'radio',
        custom: true,
        options: [
          { value: 'overlaps', name: 'overlaps' },
          { value: 'has_overlap_participations', name: 'has_overlap_participations' },
          { value: 'exceeds', name: 'exceeds' },
        ],
      };
      state.options.comment_status = {
        format: 'wording',
        label: 'comment_status',
        element: 'radio',
        custom: true,
        options: [
          { value: 'without', name: 'without' },
          { value: 'ready', name: 'ready' },
          { value: 'open', name: 'open' },
          { value: 'problem', name: 'problem' },
          { value: 'error', name: 'error' },
        ],
      };
      state.options.participation_status = {
        format: 'wording',
        label: 'participation_status',
        element: 'radio',
        custom: true,
        options: [
          { value: 'not_invited', name: 'not_invited' },
          { value: 'invited', name: 'invited' },
          { value: 'confirmed', name: 'confirmed' },
          { value: 'declined', name: 'declined' },
        ],
      };
      state.options.session_persons = {
        format: 'wording',
        label: 'session_persons',
        element: 'radio',
        custom: true,
        options: [
          { value: 'yes', name: 'yes' },
          { value: 'no', name: 'no' },
        ],
      };
    },

    async addSessions({ getters, commit, dispatch }) {
      const sessions = await dispatch('getSessions');
      commit('setProgress', false, { root: true });
      commit('setSessions', sessions.data.items);
      commit('setOffset', getters.sessions.length);
    },

    async sortSessions({ rootState, commit, dispatch, getters }, data) {
      if (!rootState.progress) {
        let order = getters.sort.column === data.column 
          ? getters.sort.order === 'asc' ? 'desc' : 'asc'
          : 'asc';
        let columns = data.columns.reduce((carry, column) => { 
          carry.push({ column: column, order: order });
          return carry;
        }, []);
        commit('setSort', { column: data.column, columns: columns, order: order });
        commit('clearList');
        await dispatch('getList');
      }
    },

    async toggleSession({ state, commit, dispatch }, params) {
      if (params.session.day_id && params.session.start_time && params.session.end_time && params.session.room_id) {
        const values = {};
        if (params.mode === 'program') {
          values.publish = !params.session.publish;
          values.publish_app = !params.session.publish;
        }
        if (params.mode === 'app') {
          values.publish_app = !params.session.publish_app;
        }
        await Vue.axios.put(API_URL + '/sessions/' + params.session.id, values);
        state.offset = 0;
        const sessions = await dispatch('getSessions');
        state.sessions = [];
        commit('setProgress', false, { root: true });
        commit('setSessions', sessions.data.items);
        commit('setOffset', state.sessions.length);
      }
    },

    async updateSession({ state, commit, dispatch }, params) {
      const values = {};
      values.code = params.code;
      await Vue.axios.put(API_URL + '/sessions/' + params.id, values);
      state.offset = 0;
      const sessions = await dispatch('getSessions');
      state.sessions = [];
      commit('setProgress', false, { root: true });
      commit('setSessions', sessions.data.items);
      commit('setOffset', state.sessions.length);
    },

  }
}
